import { useEffect, useState, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShoppingCart, Menu, X, User, LogOut, BookOpen, FileText, Calendar, ChevronRight, Plus, Minus, Trash2, Loader2, Download, LayoutDashboard, Package, Users, BarChart3, Settings, Tags, GripVertical, Bold, Italic, List, ListOrdered, Copy, Ticket, KeyRound, Star, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rich Text Editor Component for Markdown
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const textareaRef = useRef(null);

  const insertFormat = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange(newText);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatBold = () => insertFormat('**', '**');
  const formatItalic = () => insertFormat('*', '*');
  const formatBulletList = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newText = value.substring(0, lineStart) + '- ' + value.substring(lineStart);
    onChange(newText);
  };
  const formatNumberedList = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newText = value.substring(0, lineStart) + '1. ' + value.substring(lineStart);
    onChange(newText);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1 p-1 bg-muted rounded-t-md border border-b-0 border-border">
        <Button type="button" variant="ghost" size="sm" onClick={formatBold} title="Gras" className="h-8 w-8 p-0">
          <Bold className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={formatItalic} title="Italique" className="h-8 w-8 p-0">
          <Italic className="w-4 h-4" />
        </Button>
        <div className="w-px bg-border mx-1" />
        <Button type="button" variant="ghost" size="sm" onClick={formatBulletList} title="Liste à puces" className="h-8 w-8 p-0">
          <List className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={formatNumberedList} title="Liste numérotée" className="h-8 w-8 p-0">
          <ListOrdered className="w-4 h-4" />
        </Button>
      </div>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-[150px] rounded-t-none border-t-0"
        data-testid="input-description"
      />
      <p className="text-xs text-muted-foreground">
        Utilisez **texte** pour le gras, *texte* pour l'italique, - pour les listes à puces
      </p>
    </div>
  );
};

// Markdown renderer component for product descriptions
const normalizeMarkdown = (text = "") => text.replace(/^[ \t]*•[ \t]*/gm, "- ");

// Strip markdown syntax for plain-text previews (cards)
const stripMarkdown = (text = "") => text
  .replace(/\*\*(.*?)\*\*/g, "$1")
  .replace(/\*(.*?)\*/g, "$1")
  .replace(/^#+\s*/gm, "")
  .replace(/^[-•*]\s+/gm, "")
  .replace(/^\d+\.\s+/gm, "")
  .replace(/\s*\n+\s*/g, " · ");

const ProductDescription = ({ content, className = "" }) => {
  if (!content) return null;
  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={{
          p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {normalizeMarkdown(content)}
      </ReactMarkdown>
    </div>
  );
};
// Context
const AuthContext = createContext(null);
const CartContext = createContext(null);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const processOAuthSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, loginWithGoogle, processOAuthSession, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Provider
const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        return prev.map(item => 
          item.product_id === product.product_id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success("Produit ajouté au panier");
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, total, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);
const useCart = () => useContext(CartContext);

// Components
const Header = () => {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(console.error);
  }, []);

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">T</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">Institut Tamurt</h1>
              <p className="text-xs text-muted-foreground">Académie de langues</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/catalogue" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth" data-testid="nav-catalogue">Catalogue</Link>
            {categories.map(cat => (
              <Link 
                key={cat.category_id} 
                to={`/catalogue?type=${cat.slug}`} 
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth" 
                data-testid={`nav-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/cart" className="relative p-2 hover:bg-accent rounded-full transition-smooth" data-testid="cart-button">
              <ShoppingCart className="w-5 h-5 text-foreground" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-primary-foreground text-sm">{user.name?.charAt(0)}</span>
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                    <User className="w-4 h-4 mr-2" /> Mon compte
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders">
                    <Package className="w-4 h-4 mr-2" /> Mes commandes
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Administration
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button variant="default" className="bg-primary text-primary-foreground" data-testid="login-button">
                  Connexion
                </Button>
              </Link>
            )}

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-4">
              <Link to="/catalogue" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Catalogue</Link>
              {categories.map(cat => (
                <Link 
                  key={cat.category_id} 
                  to={`/catalogue?type=${cat.slug}`} 
                  className="text-sm font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const Footer = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(console.error);
  }, []);

  return (
    <footer className="bg-foreground text-background py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-lg font-semibold mb-4">Institut Tamurt</h3>
            <p className="text-sm opacity-80">Académie de langues dédiée à la transmission et à la valorisation des cultures Amazigh.</p>
          </div>
          <div>
            <h4 className="font-medium mb-4">Liens rapides</h4>
            <ul className="space-y-2 text-sm opacity-80">
              <li><Link to="/catalogue" className="hover:opacity-100">Catalogue</Link></li>
              {categories.map(cat => (
                <li key={cat.category_id}>
                  <Link to={`/catalogue?type=${cat.slug}`} className="hover:opacity-100">{cat.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-4">Contact</h4>
            <p className="text-sm opacity-80">contact@institut-tamurt.com</p>
          </div>
        </div>
        <div className="border-t border-background/20 mt-8 pt-8 text-center text-sm opacity-60">
          © {new Date().getFullYear()} Institut Tamurt. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
};

// Pages
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data.slice(0, 6))).catch(console.error);
  }, []);

  const typeIcons = { course: BookOpen, support: FileText, seminar: Calendar };
  const typeLabels = { course: "Cours", support: "Support", seminar: "Séminaire" };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/3653f3ee-2722-48c4-97ee-332777b6a42c/images/50c8dd10782fb39875dfa69c3180910ccb3fb82d0696f2ddc041da26ba920930.png')` }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.3)' }}>
            Institut Tamurt
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-8 font-light" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.3)' }}>
            Académie de langues · Apprenez, découvrez, transmettez
          </p>
          <Button onClick={() => document.getElementById('notre-histoire')?.scrollIntoView({ behavior: 'smooth' })} className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg" data-testid="cta-discover">
            Découvrez notre histoire <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="categories-section">
        <h2 className="text-2xl sm:text-3xl text-center mb-12">Nos formations</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { type: 'course', title: 'Cours', desc: 'Formations dispensées par nos professeurs experts', image: 'https://images.unsplash.com/photo-1755718669459-a8691dd613de?w=600' },
            { type: 'support', title: 'Supports de cours', desc: 'Documents PDF téléchargeables pour approfondir', image: 'https://static.prod-images.emergentagent.com/jobs/3653f3ee-2722-48c4-97ee-332777b6a42c/images/12cd2a0897f9d0da08139bfad4bea2841e6da1607f350f9496b383eba8163cad.png' },
            { type: 'seminar', title: 'Séminaires', desc: 'Sessions thématiques pour explorer en profondeur', image: 'https://images.unsplash.com/photo-1758691736490-03d39c292d7a?w=600' }
          ].map((cat, idx) => (
            <div key={cat.type} className="group cursor-pointer animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }} onClick={() => navigate(`/catalogue?type=${cat.type}`)} data-testid={`category-${cat.type}`}>
              <div className="relative h-64 rounded-lg overflow-hidden mb-4">
                <img src={cat.image} alt={cat.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
              </div>
              <h3 className="text-xl font-medium mb-2">{cat.title}</h3>
              <p className="text-muted-foreground text-sm">{cat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl text-center mb-12">Produits populaires</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {products.map((product) => {
                const Icon = typeIcons[product.product_type] || BookOpen;
                return (
                  <div key={product.product_id} className="product-card bg-card rounded-lg overflow-hidden border border-border animate-fade-in" data-testid={`product-${product.product_id}`}>
                    <div className="relative h-48 bg-muted">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon className="w-16 h-16 text-muted-foreground/30" />
                        </div>
                      )}
                      <Badge className="absolute top-3 left-3 bg-primary/90">{typeLabels[product.product_type]}</Badge>
                      {product.is_full && <Badge variant="destructive" className="absolute top-3 right-3">Complet</Badge>}
                    </div>
                    <div className="p-5">
                      <h3 className="font-medium text-lg mb-2 line-clamp-1">{product.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{stripMarkdown(product.description)}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-semibold text-primary">{product.price.toFixed(2)} €</span>
                        {product.is_full ? (
                          <Button size="sm" variant="secondary" disabled data-testid={`add-cart-${product.product_id}`}>Complet</Button>
                        ) : (
                          <Button size="sm" onClick={() => addToCart(product)} data-testid={`add-cart-${product.product_id}`}>
                            Ajouter
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-10">
              <Button variant="outline" onClick={() => navigate('/catalogue')} data-testid="view-all-products">
                Voir tout le catalogue <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* About */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="about-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl mb-6">À propos de l'Institut</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              L'Institut Tamurt est dédié à la transmission et à la valorisation des cultures et langues Amazigh. Notre mission est de rendre accessible à tous l'apprentissage de cette richesse culturelle millénaire.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Nos formations sont conçues par des experts passionnés, combinant tradition et pédagogie moderne pour vous offrir une expérience d'apprentissage unique.
            </p>
          </div>
          <div className="rounded-lg overflow-hidden">
            <img src="https://images.unsplash.com/photo-1731752301975-708299cae151?w=800" alt="Architecture" className="w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      {/* Notre Histoire */}
      <section id="notre-histoire" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="histoire-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl text-center mb-12 text-primary">Notre histoire</h2>
          
          <div className="prose prose-lg max-w-none text-foreground/90 space-y-6">
            <p className="text-lg leading-relaxed">
              Je suis <strong>Géraldine</strong>, franco-algérienne, diplômée d'un Master Meef (Métiers de l'Enseignement, de l'Education et de la Formation), enseignante et fondatrice de l'Institut Tamurt.
            </p>

            <p className="leading-relaxed">
              Depuis toujours, je crois profondément que la langue est bien plus qu'un simple outil de communication.<br />
              Elle est un <em>héritage</em>. Une <em>mémoire vivante</em>. Un <em>lien invisible</em> entre les générations.
            </p>

            <p className="leading-relaxed">
              Pourtant, comme beaucoup, je n'ai pas grandi avec la langue de mon père. Perdue au milieu de ma double-culture.<br />
              Ce manque, discret mais persistant, m'a accompagnée au fil des années…<br />
              Dans les silences, dans les conversations que je ne comprenais pas entièrement, dans ce sentiment d'être à la fois proche… et à distance.
            </p>

            <div className="border-l-4 border-primary pl-6 my-8 italic text-muted-foreground">
              <p>Et puis, il y a eu un tournant.<br />Lorsque je suis devenue mère.</p>
            </div>

            <p className="leading-relaxed">
              C'est à ce moment précis que ce besoin est devenu une évidence.<br />
              La peur de transmettre ce manque à mes propres enfants.<br />
              Le désir profond de leur offrir ce que moi, je n'avais pas reçu.<br />
              Une langue, oui… mais surtout un <strong>ancrage</strong>, une <strong>continuité</strong>, une <strong>identité assumée</strong>.
            </p>

            <p className="text-xl font-medium text-primary text-center my-10">
              C'est de cette histoire qu'est né l'Institut Tamurt.
            </p>

            <p className="leading-relaxed">
              Aujourd'hui, je m'entoure également de personnes qui incarnent cette exigence de transmission.<br />
              Je travaille aux côtés de <strong>Djouhra</strong>, enseignante native de la wilaya de Tizi Ouzou, profondément ancrée dans sa culture et sa langue.<br />
              Djouhra enseigne non seulement sa langue maternelle le kabyle, mais aussi le darija algérien et l'arabe littéraire.<br />
              Très pédagogue, son écoute, sa patience et sa bienveillance font d'elle une alliée précieuse dans l'accompagnement de nos élèves.
            </p>

            <p className="leading-relaxed">
              Ensemble, nous avons imaginé un espace d'apprentissage exigeant et profondément humain, pour celles et ceux qui souhaitent se réapproprier leur langue d'origine avec justesse et élégance.
            </p>

            <p className="leading-relaxed font-medium">
              Ici, on ne se contente pas d'apprendre à parler.<br />
              On redonne du sens, de la valeur, et une place à ce qui semblait parfois inaccessible.
            </p>

            <p className="leading-relaxed">
              À travers nos formations immersives et une pédagogie structurée, nous vous accompagnons dans un véritable cheminement : celui de la reconnexion à votre culture, à votre histoire, et à vous-même.
            </p>

            <div className="bg-card rounded-lg p-8 mt-10 border border-border text-center">
              <p className="text-lg mb-2">Parce que comprendre une langue, c'est aussi se comprendre.</p>
              <p className="text-lg mb-4">Parce qu'il n'est jamais trop tard pour renouer avec ce qui nous appartient.</p>
              <p className="text-xl font-semibold text-primary mt-6">
                Si vous ressentez cet appel, alors l'Institut Tamurt est fait pour vous.
              </p>
              <Button onClick={() => navigate('/catalogue')} className="mt-6" data-testid="cta-formations">
                Découvrir nos formations <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CataloguePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const filterType = searchParams.get('type');
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Load categories
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(console.error);
    axios.get(`${API}/reviews/summary`).then(r => setRatings(r.data)).catch(console.error);
  }, []);

  // Load products based on filter
  useEffect(() => {
    setLoading(true);
    const url = filterType ? `${API}/products?category=${filterType}` : `${API}/products`;
    axios.get(url).then(r => { setProducts(r.data); setLoading(false); }).catch(e => { console.error(e); setLoading(false); });
  }, [filterType, location.search]);

  // Get category info for display
  const currentCategory = categories.find(c => c.slug === filterType);
  const pageTitle = currentCategory?.name || (filterType ? "Catalogue" : "Catalogue complet");

  // Icon mapping
  const iconMap = { BookOpen, FileText, Calendar, Package, Users };
  const getIcon = (iconName) => iconMap[iconName] || BookOpen;

  const visibleProducts = products.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (p.title || '').toLowerCase().includes(q)
      || (p.description || '').toLowerCase().includes(q)
      || (p.professor_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="catalogue-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-light">{pageTitle}</h1>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant={!filterType ? "default" : "outline"} 
            size="sm" 
            onClick={() => navigate('/catalogue')} 
            data-testid="filter-all"
          >
            Tout
          </Button>
          {categories.map(cat => (
            <Button 
              key={cat.category_id}
              variant={filterType === cat.slug ? "default" : "outline"} 
              size="sm" 
              onClick={() => navigate(`/catalogue?type=${cat.slug}`)} 
              data-testid={`filter-${cat.slug}`}
            >
              {cat.name}
            </Button>
          ))}
        </div>
      </div>

      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher une formation par nom ou mot-clé..."
          className="pl-10"
          data-testid="catalog-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : visibleProducts.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="no-results">
          {search.trim() ? "Aucune formation ne correspond à votre recherche." : "Aucun produit disponible pour le moment."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleProducts.map((product) => {
            // Get category for this product
            const productCat = categories.find(c => c.category_id === product.category_id || c.slug === product.product_type);
            const Icon = productCat ? getIcon(productCat.icon) : BookOpen;
            const categoryLabel = productCat?.name || product.product_type || 'Produit';
            
            return (
              <div key={product.product_id} className="product-card bg-card rounded-lg overflow-hidden border border-border" data-testid={`catalog-product-${product.product_id}`}>
                <div className="relative h-48 bg-muted cursor-pointer" onClick={() => navigate(`/product/${product.product_id}`)}>
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-16 h-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <Badge className="absolute top-3 left-3 bg-primary/90">{categoryLabel}</Badge>
                  {product.is_full && <Badge variant="destructive" className="absolute top-3 right-3">Complet</Badge>}
                </div>
                <div className="p-5">
                  <h3 className="font-medium text-lg mb-2 line-clamp-1 cursor-pointer hover:text-primary" onClick={() => navigate(`/product/${product.product_id}`)}>{product.title}</h3>
                  {ratings[product.product_id] && (
                    <div className="flex items-center gap-1.5 mb-2" data-testid={`rating-${product.product_id}`}>
                      <Stars rating={ratings[product.product_id].average} />
                      <span className="text-xs text-muted-foreground">{ratings[product.product_id].average} ({ratings[product.product_id].count})</span>
                    </div>
                  )}
                  {(product.professor_name || product.duration) && (
                    <p className="text-xs text-muted-foreground mb-2">
                      {product.professor_name && <>Prof. {product.professor_name}</>}
                      {product.professor_name && product.duration && " · "}
                      {product.duration && <>Durée : {product.duration}</>}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{stripMarkdown(product.description)}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-semibold text-primary">{product.price.toFixed(2)} €</span>
                    {product.is_full ? (
                      <Button size="sm" variant="secondary" disabled data-testid={`add-cart-catalog-${product.product_id}`}>Complet</Button>
                    ) : (
                      <Button size="sm" onClick={() => addToCart(product)} data-testid={`add-cart-catalog-${product.product_id}`}>Ajouter</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProductPage = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const location = useLocation();
  const productId = location.pathname.split('/').pop();

  useEffect(() => {
    axios.get(`${API}/products/${productId}`).then(r => { setProduct(r.data); setLoading(false); }).catch(e => { console.error(e); setLoading(false); });
  }, [productId]);

  const typeLabels = { course: "Cours", support: "Support PDF", seminar: "Séminaire" };
  const typeIcons = { course: BookOpen, support: FileText, seminar: Calendar };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Produit non trouvé</div>;

  const Icon = typeIcons[product.product_type] || BookOpen;

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-testid="product-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="relative rounded-lg overflow-hidden bg-muted aspect-square">
          {product.image_url ? (
            <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon className="w-32 h-32 text-muted-foreground/20" />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="w-fit">{typeLabels[product.product_type]}</Badge>
            {product.is_full && <Badge variant="destructive" data-testid="product-full-badge">Complet</Badge>}
          </div>
          <h1 className="text-3xl font-light mb-4" data-testid="product-title">{product.title}</h1>
          {product.professor_name && <p className="text-muted-foreground mb-2">Professeur: {product.professor_name}</p>}
          {product.duration && <p className="text-muted-foreground mb-4">Durée: {product.duration}</p>}
          <div className="text-muted-foreground mb-8 leading-relaxed">
            <ProductDescription content={product.description} />
          </div>
          <div className="mt-auto">
            <div className="text-3xl font-semibold text-primary mb-6" data-testid="product-price">{product.price.toFixed(2)} €</div>
            {product.is_full ? (
              <>
                <div className="bg-destructive/10 text-destructive rounded-lg p-4 mb-4 text-sm" data-testid="full-notice">
                  Cette formation est actuellement complète. Les inscriptions sont closes.
                </div>
                <Button size="lg" className="w-full" disabled data-testid="add-to-cart-main">
                  Complet — inscriptions closes
                </Button>
              </>
            ) : (
              <Button size="lg" className="w-full" onClick={() => addToCart(product)} data-testid="add-to-cart-main">
                Ajouter au panier
              </Button>
            )}
          </div>
        </div>
      </div>
      <ReviewsSection productId={product.product_id} />
    </div>
  );
};

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, total, clearCart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const navigate = useNavigate();

  const discount = promo ? (promo.discount_type === 'percent' ? Math.round(total * promo.discount_value) / 100 : Math.min(promo.discount_value, total)) : 0;
  const finalTotal = Math.max(total - discount, 0);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const r = await axios.post(`${API}/promo/validate`, { code: promoInput.trim(), cart_total: total });
      setPromo(r.data);
      toast.success(`Code ${r.data.code} appliqué !`);
    } catch (error) {
      setPromo(null);
      toast.error(error.response?.data?.detail || "Code promo invalide");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour continuer");
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        cart_items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        origin_url: window.location.origin,
        promo_code: promo?.code || null
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      
      clearCart();
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center" data-testid="cart-empty">
        <ShoppingCart className="w-20 h-20 mx-auto text-muted-foreground/30 mb-6" />
        <h1 className="text-2xl mb-4">Votre panier est vide</h1>
        <p className="text-muted-foreground mb-8">Découvrez nos formations et commencez votre apprentissage</p>
        <Button onClick={() => navigate('/catalogue')} data-testid="browse-catalogue">Parcourir le catalogue</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-testid="cart-page">
      <h1 className="text-3xl font-light mb-8">Votre panier</h1>
      <div className="space-y-4 mb-8">
        {cart.map(item => (
          <div key={item.product_id} className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border" data-testid={`cart-item-${item.product_id}`}>
            <div className="w-20 h-20 bg-muted rounded overflow-hidden flex-shrink-0">
              {item.image_url ? <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-8 h-8 text-muted-foreground/30" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.price.toFixed(2)} €</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} data-testid={`decrease-${item.product_id}`}><Minus className="w-4 h-4" /></Button>
              <span className="w-8 text-center">{item.quantity}</span>
              <Button variant="outline" size="icon" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} data-testid={`increase-${item.product_id}`}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="w-24 text-right font-medium">{(item.price * item.quantity).toFixed(2)} €</div>
            <Button variant="ghost" size="icon" onClick={() => removeFromCart(item.product_id)} data-testid={`remove-${item.product_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <Label htmlFor="promo-code" className="text-sm mb-2 block">Code promotionnel</Label>
          <div className="flex gap-2">
            <Input
              id="promo-code"
              value={promoInput}
              onChange={e => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Entrez votre code"
              disabled={!!promo}
              data-testid="promo-code-input"
            />
            {promo ? (
              <Button variant="outline" onClick={() => { setPromo(null); setPromoInput(''); }} data-testid="remove-promo-button">Retirer</Button>
            ) : (
              <Button variant="outline" onClick={applyPromo} disabled={promoLoading || !promoInput.trim()} data-testid="apply-promo-button">
                {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
              </Button>
            )}
          </div>
          {promo && (
            <p className="text-sm text-primary mt-2 flex items-center gap-1" data-testid="promo-applied">
              <Ticket className="w-4 h-4" /> Code {promo.code} appliqué : {promo.discount_type === 'percent' ? `-${promo.discount_value}%` : `-${promo.discount_value.toFixed(2)} €`}
            </p>
          )}
        </div>
        {promo && (
          <>
            <div className="flex justify-between items-center mb-2 text-sm text-muted-foreground">
              <span>Sous-total</span>
              <span data-testid="cart-subtotal">{total.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between items-center mb-4 text-sm text-primary">
              <span>Remise ({promo.code})</span>
              <span data-testid="cart-discount">-{discount.toFixed(2)} €</span>
            </div>
          </>
        )}
        <div className="flex justify-between items-center mb-6">
          <span className="text-lg">Total</span>
          <span className="text-2xl font-semibold text-primary" data-testid="cart-total">{finalTotal.toFixed(2)} €</span>
        </div>
        <Button className="w-full" size="lg" onClick={handleCheckout} disabled={loading} data-testid="checkout-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          Procéder au paiement
        </Button>
      </div>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const [status, setStatus] = useState('loading');
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };
    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen py-20 px-4 text-center" data-testid="payment-result-page">
      {status === 'loading' && (
        <>
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
          <h1 className="text-2xl mb-2">Vérification du paiement...</h1>
          <p className="text-muted-foreground">Veuillez patienter</p>
        </>
      )}
      {status === 'success' && (
        <>
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h1 className="text-2xl mb-2" data-testid="payment-success">Paiement réussi !</h1>
          <p className="text-muted-foreground mb-8">Merci pour votre achat. Votre facture a été générée.</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/orders')} data-testid="view-orders">Voir mes commandes</Button>
            <Button variant="outline" onClick={() => navigate('/catalogue')}>Continuer mes achats</Button>
          </div>
        </>
      )}
      {(status === 'error' || status === 'timeout' || status === 'expired') && (
        <>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl mb-2">Erreur de paiement</h1>
          <p className="text-muted-foreground mb-8">Une erreur est survenue. Veuillez réessayer.</p>
          <Button onClick={() => navigate('/cart')}>Retour au panier</Button>
        </>
      )}
    </div>
  );
};

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      toast.success(isLogin ? "Connexion réussie" : "Compte créé avec succès");
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'authentification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light mb-2">{isLogin ? "Connexion" : "Inscription"}</h1>
          <p className="text-muted-foreground">Institut Tamurt - Académie de langues</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-8">
          <Button variant="outline" className="w-full mb-6" onClick={loginWithGoogle} data-testid="google-login">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuer avec Google
          </Button>
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">ou</span></div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" value={name} onChange={e => setName(e.target.value)} required data-testid="input-name" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="input-email" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="input-password" />
              {isLogin && (
                <div className="text-right mt-1">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</Link>
                </div>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-auth">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {isLogin ? "Se connecter" : "Créer un compte"}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}
            <button type="button" className="text-primary ml-1 hover:underline" onClick={() => setIsLogin(!isLogin)} data-testid="toggle-auth">
              {isLogin ? "S'inscrire" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const { processOAuthSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    
    if (sessionId) {
      processOAuthSession(sessionId)
        .then(() => {
          toast.success("Connexion réussie");
          navigate('/');
        })
        .catch((error) => {
          toast.error("Erreur d'authentification");
          navigate('/login');
        });
    } else {
      navigate('/login');
    }
  }, [processOAuthSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setOrders(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [user, navigate]);

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-testid="orders-page">
      <h1 className="text-3xl font-light mb-8">Mes commandes</h1>
      {orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground mb-6">Aucune commande pour le moment</p>
          <Button onClick={() => navigate('/catalogue')}>Découvrir nos formations</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.order_id} className="bg-card rounded-lg border border-border p-6" data-testid={`order-${order.order_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <p className="font-medium">Commande #{order.order_id.slice(-8).toUpperCase()}</p>
                  <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {order.payment_status === 'paid' ? 'Payée' : 'En attente'}
                  </Badge>
                  <span className="font-semibold text-primary">{order.total_amount.toFixed(2)} €</span>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.title} x{item.quantity}</span>
                    <span>{(item.price * item.quantity).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              {order.invoice_id && (
                <Button variant="outline" size="sm" onClick={() => downloadInvoice(order.invoice_id)} data-testid={`download-invoice-${order.order_id}`}>
                  <Download className="w-4 h-4 mr-2" /> Télécharger la facture
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Account Page
const AccountPage = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    // Fetch full profile
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setProfile(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [user, navigate]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, {
        name: profile.name,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        postal_code: profile.postal_code,
        country: profile.country
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setProfile(response.data);
      toast.success("Profil mis à jour");
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success("Mot de passe modifié");
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const isOAuthUser = !profile?.password_hash && user?.picture;

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-testid="account-page">
      <h1 className="text-3xl font-light mb-8">Mon compte</h1>
      
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Sécurité</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-xl font-medium mb-6">Informations personnelles</h2>
            
            {user?.picture && (
              <div className="flex items-center gap-4 mb-6">
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                <div>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  {isOAuthUser && <Badge variant="secondary" className="mt-1">Connecté via Google</Badge>}
                </div>
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nom complet</Label>
                  <Input 
                    id="name" 
                    value={profile?.name || ''} 
                    onChange={e => setProfile({...profile, name: e.target.value})}
                    data-testid="input-profile-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={profile?.email || ''} disabled className="bg-muted" />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input 
                    id="phone" 
                    value={profile?.phone || ''} 
                    onChange={e => setProfile({...profile, phone: e.target.value})}
                    placeholder="+33 6 12 34 56 78"
                    data-testid="input-profile-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Pays</Label>
                  <Input 
                    id="country" 
                    value={profile?.country || ''} 
                    onChange={e => setProfile({...profile, country: e.target.value})}
                    placeholder="France"
                    data-testid="input-profile-country"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Adresse</Label>
                <Input 
                  id="address" 
                  value={profile?.address || ''} 
                  onChange={e => setProfile({...profile, address: e.target.value})}
                  placeholder="123 Rue de la Paix"
                  data-testid="input-profile-address"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Ville</Label>
                  <Input 
                    id="city" 
                    value={profile?.city || ''} 
                    onChange={e => setProfile({...profile, city: e.target.value})}
                    placeholder="Paris"
                    data-testid="input-profile-city"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input 
                    id="postal_code" 
                    value={profile?.postal_code || ''} 
                    onChange={e => setProfile({...profile, postal_code: e.target.value})}
                    placeholder="75001"
                    data-testid="input-profile-postal"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={saving} data-testid="save-profile">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Enregistrer les modifications
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-xl font-medium mb-6">Sécurité du compte</h2>

            {isOAuthUser ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                </div>
                <p className="text-muted-foreground">Vous êtes connecté via Google.</p>
                <p className="text-sm text-muted-foreground mt-2">La gestion du mot de passe se fait via votre compte Google.</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div>
                  <Label htmlFor="current_password">Mot de passe actuel</Label>
                  <Input 
                    id="current_password" 
                    type="password" 
                    value={passwordForm.current_password}
                    onChange={e => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    required
                    data-testid="input-current-password"
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <Input 
                    id="new_password" 
                    type="password" 
                    value={passwordForm.new_password}
                    onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    required
                    data-testid="input-new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                  <Input 
                    id="confirm_password" 
                    type="password" 
                    value={passwordForm.confirm_password}
                    onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    required
                    data-testid="input-confirm-password"
                  />
                </div>
                <div className="pt-4">
                  <Button type="submit" disabled={changingPassword} data-testid="change-password">
                    {changingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Modifier le mot de passe
                  </Button>
                </div>
              </form>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Admin Pages
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setStats(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-8">
          <h1 className="text-2xl font-light mb-8">Tableau de bord</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6" data-testid="stat-revenue">
              <p className="text-sm text-muted-foreground mb-2">Chiffre d'affaires</p>
              <p className="text-2xl font-semibold text-primary">{stats?.total_revenue?.toFixed(2) || 0} €</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6" data-testid="stat-orders">
              <p className="text-sm text-muted-foreground mb-2">Commandes payées</p>
              <p className="text-2xl font-semibold">{stats?.paid_orders || 0}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6" data-testid="stat-users">
              <p className="text-sm text-muted-foreground mb-2">Utilisateurs</p>
              <p className="text-2xl font-semibold">{stats?.total_users || 0}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6" data-testid="stat-products">
              <p className="text-sm text-muted-foreground mb-2">Produits</p>
              <p className="text-2xl font-semibold">{stats?.total_products || 0}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/admin/products')} data-testid="manage-products">Gérer les produits</Button>
            <Button variant="outline" onClick={() => navigate('/admin/orders')} data-testid="view-admin-orders">Voir les commandes</Button>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchProducts = () => {
    axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setProducts(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  };

  const fetchCategories = () => {
    axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setCategories(r.data))
      .catch(console.error);
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchProducts();
    fetchCategories();
  }, [user, navigate]);

  const handleSave = async (formData) => {
    try {
      if (editProduct?.product_id) {
        await axios.put(`${API}/admin/products/${editProduct.product_id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Produit mis à jour");
      } else {
        await axios.post(`${API}/admin/products`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Produit créé");
      }
      fetchProducts();
      setIsDialogOpen(false);
      setEditProduct(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDuplicate = (product) => {
    // Create a copy of the product without the ID
    const duplicatedProduct = {
      ...product,
      product_id: undefined,
      title: `Copie de ${product.title}`,
      is_active: false // Set as inactive by default
    };
    setEditProduct(duplicatedProduct);
    setIsDialogOpen(true);
    toast.info("Produit dupliqué - Modifiez et enregistrez");
  };

  // Get category name for display
  const getCategoryName = (product) => {
    const cat = categories.find(c => c.category_id === product.category_id || c.slug === product.product_type);
    return cat?.name || product.product_type || 'Non catégorisé';
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-products">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-light">Gestion des produits</h1>
            <Button onClick={() => { setEditProduct({}); setIsDialogOpen(true); }} data-testid="add-product">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un produit
            </Button>
          </div>

          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(product => (
                  <TableRow key={product.product_id} data-testid={`admin-product-${product.product_id}`}>
                    <TableCell className="font-medium">{product.title}</TableCell>
                    <TableCell><Badge variant="secondary">{getCategoryName(product)}</Badge></TableCell>
                    <TableCell>{product.price.toFixed(2)} €</TableCell>
                    <TableCell><Badge variant={product.is_active ? "default" : "outline"}>{product.is_active ? "Actif" : "Inactif"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(product)} title="Dupliquer" data-testid={`duplicate-${product.product_id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditProduct(product); setIsDialogOpen(true); }} data-testid={`edit-${product.product_id}`}>Modifier</Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(product.product_id)} data-testid={`delete-${product.product_id}`}>Supprimer</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editProduct?.product_id ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
              </DialogHeader>
              <ProductForm product={editProduct} categories={categories} onSave={handleSave} onCancel={() => setIsDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

const ProductForm = ({ product, categories = [], onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: product?.title || '',
    description: product?.description || '',
    price: product?.price || '',
    category_id: product?.category_id || product?.product_type || (categories[0]?.category_id || ''),
    image_url: product?.image_url || '',
    professor_name: product?.professor_name || '',
    duration: product?.duration || '',
    file_id: product?.file_id || '',
    is_active: product?.is_active !== false
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Update category_id when categories load
  useEffect(() => {
    if (!form.category_id && categories.length > 0) {
      setForm(prev => ({ ...prev, category_id: categories[0].category_id }));
    }
  }, [categories]);

  // Get current category for conditional fields
  const currentCategory = categories.find(c => c.category_id === form.category_id || c.slug === form.category_id);
  const showFileField = currentCategory?.slug === 'support' || form.category_id === 'support' || form.category_id === 'cat_support';

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, file_id: response.data.file_id }));
      toast.success("Fichier uploadé");
    } catch (error) {
      toast.error("Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log('File selected:', file.name, file.type, file.size);
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez JPG, PNG, WebP ou GIF.");
      return;
    }
    
    // Validate file size (max 10MB for larger images)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }
    
    setUploadingImage(true);
    toast.info("Upload en cours...");
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Starting upload...');
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 60000 // 60 second timeout for large files
      });
      console.log('Upload response:', response.data);
      // Build full URL from relative path
      const imageUrl = response.data.url.startsWith('/') 
        ? `${BACKEND_URL}${response.data.url}` 
        : response.data.url;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploadée avec succès!");
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || "Erreur d'upload de l'image";
      toast.error(errorMsg);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, display_order: parseInt(form.display_order) || 0 }); }} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label>Catégorie</Label>
        <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
          <SelectTrigger data-testid="select-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Titre</Label>
        <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="input-title" />
      </div>
      <div>
        <Label>Description</Label>
        <RichTextEditor 
          value={form.description} 
          onChange={val => setForm({ ...form, description: val })} 
          placeholder="Décrivez votre produit... Utilisez **gras**, *italique*, - listes"
        />
      </div>
      <div>
        <Label>Prix (€)</Label>
        <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) })} required data-testid="input-price" />
      </div>
      
      {/* Image Upload Section */}
      <div className="space-y-3">
        <Label>Image du produit</Label>
        <div className="flex gap-3 items-start">
          {form.image_url && (
            <div className="relative w-20 h-20 rounded border border-border overflow-hidden flex-shrink-0">
              <img src={form.image_url} alt="Aperçu" className="w-full h-full object-cover" />
              <button 
                type="button" 
                onClick={() => setForm({ ...form, image_url: '' })}
                className="absolute top-0 right-0 bg-destructive text-white w-5 h-5 flex items-center justify-center text-xs rounded-bl"
              >
                ×
              </button>
            </div>
          )}
          <div className="flex-1 space-y-2">
            <div className="flex gap-2">
              <label className="flex-1">
                <div className={`border-2 border-dashed border-border rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
                  {uploadingImage ? (
                    <span className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Upload en cours...
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      📷 Cliquez pour uploader une image
                    </span>
                  )}
                </div>
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/webp,image/gif" 
                  onChange={handleImageUpload} 
                  disabled={uploadingImage}
                  className="hidden"
                  data-testid="input-image-upload"
                />
              </label>
            </div>
            <div className="text-xs text-muted-foreground">ou</div>
            <Input 
              value={form.image_url} 
              onChange={e => setForm({ ...form, image_url: e.target.value })} 
              placeholder="https://... (URL externe)" 
              data-testid="input-image"
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Professeur</Label>
          <Input value={form.professor_name} onChange={e => setForm({ ...form, professor_name: e.target.value })} placeholder="Nom du professeur" data-testid="input-professor" />
        </div>
        <div>
          <Label>Durée</Label>
          <Input value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} placeholder="ex: 2h30, 10 semaines" data-testid="input-duration" />
        </div>
      </div>
      <div>
        <Label>Ordre d'affichage</Label>
        <Input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} data-testid="input-display-order" />
        <p className="text-xs text-muted-foreground mt-1">Les produits sont triés du plus petit au plus grand dans leur catégorie.</p>
      </div>
      {showFileField && (
        <div>
          <Label>Fichier PDF</Label>
          <Input type="file" accept=".pdf" onChange={handleFileUpload} disabled={uploading} data-testid="input-file" />
          {uploading && <p className="text-sm text-muted-foreground mt-1">Upload en cours...</p>}
          {form.file_id && <p className="text-sm text-green-600 mt-1">✓ Fichier PDF attaché</p>}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="is_full" checked={form.is_full} onChange={e => setForm({ ...form, is_full: e.target.checked })} data-testid="checkbox-is-full" />
        <Label htmlFor="is_full">Complet — fermer les inscriptions (le produit reste visible)</Label>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
        <Label htmlFor="is_active">Produit actif</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={uploadingImage || uploading || !form.category_id} data-testid="save-product">
          {(uploadingImage || uploading) && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Enregistrer
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

// Admin Categories Page
const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCategory, setEditCategory] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchCategories = () => {
    axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setCategories(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchCategories();
  }, [user, navigate]);

  const handleSave = async (formData) => {
    try {
      if (editCategory?.category_id) {
        await axios.put(`${API}/admin/categories/${editCategory.category_id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Catégorie mise à jour");
      } else {
        await axios.post(`${API}/admin/categories`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Catégorie créée");
      }
      fetchCategories();
      setIsDialogOpen(false);
      setEditCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Catégorie supprimée");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-categories">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-light">Gestion des catégories</h1>
            <Button onClick={() => { setEditCategory({}); setIsDialogOpen(true); }} data-testid="add-category">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une catégorie
            </Button>
          </div>

          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ordre</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map(category => (
                  <TableRow key={category.category_id} data-testid={`admin-category-${category.category_id}`}>
                    <TableCell className="w-16">
                      <span className="text-muted-foreground">{category.display_order}</span>
                    </TableCell>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell className="text-muted-foreground">{category.slug}</TableCell>
                    <TableCell>
                      <Badge variant={category.is_active ? "default" : "outline"}>
                        {category.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setEditCategory(category); setIsDialogOpen(true); }} data-testid={`edit-cat-${category.category_id}`}>
                        Modifier
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(category.category_id)} data-testid={`delete-cat-${category.category_id}`}>
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editCategory?.category_id ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
              </DialogHeader>
              <CategoryForm category={editCategory} onSave={handleSave} onCancel={() => setIsDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

const CategoryForm = ({ category, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    description: category?.description || '',
    image_url: category?.image_url || '',
    icon: category?.icon || 'BookOpen',
    display_order: category?.display_order || 0,
    is_active: category?.is_active !== false
  });

  // Auto-generate slug from name
  const handleNameChange = (name) => {
    const slug = name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setForm({ ...form, name, slug: category?.slug || slug });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div>
        <Label>Nom de la catégorie</Label>
        <Input 
          value={form.name} 
          onChange={e => handleNameChange(e.target.value)} 
          required 
          placeholder="Ex: Cours de langue"
          data-testid="input-cat-name" 
        />
      </div>
      <div>
        <Label>Slug (URL)</Label>
        <Input 
          value={form.slug} 
          onChange={e => setForm({ ...form, slug: e.target.value })} 
          required 
          placeholder="cours-de-langue"
          data-testid="input-cat-slug" 
        />
        <p className="text-xs text-muted-foreground mt-1">Utilisé dans les URLs: /catalogue?type={form.slug || 'slug'}</p>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea 
          value={form.description} 
          onChange={e => setForm({ ...form, description: e.target.value })} 
          placeholder="Description de la catégorie..."
          data-testid="input-cat-description" 
        />
      </div>
      <div>
        <Label>URL de l'image</Label>
        <Input 
          value={form.image_url} 
          onChange={e => setForm({ ...form, image_url: e.target.value })} 
          placeholder="https://..."
          data-testid="input-cat-image" 
        />
      </div>
      <div>
        <Label>Icône</Label>
        <Select value={form.icon} onValueChange={v => setForm({ ...form, icon: v })}>
          <SelectTrigger data-testid="select-cat-icon"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="BookOpen">📚 Livre (Cours)</SelectItem>
            <SelectItem value="FileText">📄 Document (Supports)</SelectItem>
            <SelectItem value="Calendar">📅 Calendrier (Séminaires)</SelectItem>
            <SelectItem value="Users">👥 Personnes (Ateliers)</SelectItem>
            <SelectItem value="Package">📦 Package (Autres)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Ordre d'affichage</Label>
        <Input 
          type="number" 
          value={form.display_order} 
          onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} 
          data-testid="input-cat-order" 
        />
        <p className="text-xs text-muted-foreground mt-1">Les catégories sont triées par ordre croissant</p>
      </div>
      <div className="flex items-center gap-2">
        <input 
          type="checkbox" 
          id="cat_is_active" 
          checked={form.is_active} 
          onChange={e => setForm({ ...form, is_active: e.target.checked })} 
        />
        <Label htmlFor="cat_is_active">Catégorie active (visible dans le menu)</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" data-testid="save-category">Enregistrer</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setOrders(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-orders">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <h1 className="text-2xl font-light mb-8">Gestion des commandes</h1>
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Commande</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Produits</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => (
                  <TableRow key={order.order_id} data-testid={`admin-order-${order.order_id}`}>
                    <TableCell className="font-mono text-sm">{order.order_id.slice(-8).toUpperCase()}</TableCell>
                    <TableCell>{order.user_email}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {(order.items || []).map((it, i) => (
                          <div key={i} className="text-sm">{it.quantity} × {it.title}</div>
                        ))}
                        {order.promo_code && (
                          <div className="text-xs text-muted-foreground">Code {order.promo_code} (-{(order.discount_amount || 0).toFixed(2)} €)</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell>{order.total_amount.toFixed(2)} €</TableCell>
                    <TableCell><Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>{order.payment_status === 'paid' ? 'Payée' : 'En attente'}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setUsers(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-users">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <h1 className="text-2xl font-light mb-8">Gestion des utilisateurs</h1>
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Date d'inscription</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>{u.role}</Badge></TableCell>
                    <TableCell>{new Date(u.created_at).toLocaleDateString('fr-FR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </main>
      </div>
    </div>
  );
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, origin_url: window.location.origin });
      setSent(true);
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <KeyRound className="w-10 h-10 mx-auto text-primary mb-4" />
          <h1 className="text-3xl font-light mb-2">Mot de passe oublié</h1>
          <p className="text-muted-foreground">Institut Tamurt - Académie de langues</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <p>Si un compte existe avec l'adresse <strong>{email}</strong>, un email de réinitialisation vient d'être envoyé.</p>
              <p className="text-sm text-muted-foreground">Pensez à vérifier vos spams. Le lien est valable 1 heure.</p>
              <Link to="/login" className="text-primary hover:underline text-sm">Retour à la connexion</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">Saisissez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="input-forgot-email" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-forgot">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Envoyer le lien de réinitialisation
              </Button>
              <p className="text-center text-sm">
                <Link to="/login" className="text-primary hover:underline">Retour à la connexion</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Mot de passe réinitialisé ! Vous pouvez maintenant vous connecter.");
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4" data-testid="reset-invalid">
        <p className="text-muted-foreground mb-4">Lien de réinitialisation invalide.</p>
        <Link to="/forgot-password" className="text-primary hover:underline">Refaire une demande</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <KeyRound className="w-10 h-10 mx-auto text-primary mb-4" />
          <h1 className="text-3xl font-light mb-2">Nouveau mot de passe</h1>
          <p className="text-muted-foreground">Institut Tamurt - Académie de langues</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-pass">Nouveau mot de passe</Label>
              <Input id="new-pass" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="input-new-password" />
            </div>
            <div>
              <Label htmlFor="confirm-pass">Confirmer le mot de passe</Label>
              <Input id="confirm-pass" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6} data-testid="input-confirm-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-reset">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Réinitialiser le mot de passe
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

const PromoForm = ({ promo, onSave, onCancel }) => {
  const [form, setForm] = useState({
    code: promo?.code || '',
    discount_type: promo?.discount_type || 'percent',
    discount_value: promo?.discount_value ?? '',
    valid_from: promo?.valid_from?.slice(0, 10) || '',
    valid_until: promo?.valid_until?.slice(0, 10) || '',
    max_uses: promo?.max_uses ?? '',
    is_active: promo?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      code: form.code.trim().toUpperCase(),
      discount_value: parseFloat(form.discount_value),
      max_uses: form.max_uses === '' ? null : parseInt(form.max_uses),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Code</Label>
        <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ex: BIENVENUE10" required data-testid="input-promo-code" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Type de remise</Label>
          <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
            <SelectTrigger data-testid="select-promo-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">Pourcentage (%)</SelectItem>
              <SelectItem value="fixed">Montant fixe (€)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{form.discount_type === 'percent' ? 'Remise (%)' : 'Remise (€)'}</Label>
          <Input type="number" step="0.01" min="0" max={form.discount_type === 'percent' ? 100 : undefined} value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} required data-testid="input-promo-value" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Valide à partir du</Label>
          <Input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} data-testid="input-promo-from" />
        </div>
        <div>
          <Label>Valide jusqu'au</Label>
          <Input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} data-testid="input-promo-until" />
        </div>
      </div>
      <div>
        <Label>Nombre d'utilisations maximum</Label>
        <Input type="number" min="1" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="Laisser vide = illimité" data-testid="input-promo-max-uses" />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="promo_active" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} data-testid="checkbox-promo-active" />
        <Label htmlFor="promo_active">Code actif</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" data-testid="save-promo">Enregistrer</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPromo, setEditPromo] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchPromos = () => {
    axios.get(`${API}/admin/promo-codes`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setPromos(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchPromos();
  }, [user, navigate]);

  const handleSave = async (formData) => {
    try {
      if (editPromo?.promo_id) {
        await axios.put(`${API}/admin/promo-codes/${editPromo.promo_id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Code promo mis à jour");
      } else {
        await axios.post(`${API}/admin/promo-codes`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Code promo créé");
      }
      fetchPromos();
      setIsDialogOpen(false);
      setEditPromo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm("Supprimer ce code promo ?")) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Code promo supprimé");
      fetchPromos();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const promoStatus = (p) => {
    if (!p.is_active) return { label: 'Inactif', variant: 'outline' };
    const today = new Date().toISOString().slice(0, 10);
    if (p.valid_from && today < p.valid_from.slice(0, 10)) return { label: 'À venir', variant: 'secondary' };
    if (p.valid_until && today > p.valid_until.slice(0, 10)) return { label: 'Expiré', variant: 'outline' };
    if (p.max_uses && (p.uses || 0) >= p.max_uses) return { label: 'Épuisé', variant: 'outline' };
    return { label: 'Actif', variant: 'default' };
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-promos">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-light">Codes promotionnels</h1>
            <Button onClick={() => { setEditPromo({}); setIsDialogOpen(true); }} data-testid="add-promo">
              <Plus className="w-4 h-4 mr-2" /> Créer un code
            </Button>
          </div>

          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Remise</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Utilisations</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promos.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucun code promo. Créez-en un !</TableCell></TableRow>
                )}
                {promos.map(p => {
                  const status = promoStatus(p);
                  return (
                    <TableRow key={p.promo_id} data-testid={`admin-promo-${p.promo_id}`}>
                      <TableCell className="font-mono font-semibold">{p.code}</TableCell>
                      <TableCell>{p.discount_type === 'percent' ? `${p.discount_value} %` : `${p.discount_value.toFixed(2)} €`}</TableCell>
                      <TableCell className="text-sm">
                        {p.valid_from || p.valid_until ? (
                          <>{p.valid_from ? new Date(p.valid_from).toLocaleDateString('fr-FR') : '...'} → {p.valid_until ? new Date(p.valid_until).toLocaleDateString('fr-FR') : '...'}</>
                        ) : <span className="text-muted-foreground">Illimitée</span>}
                      </TableCell>
                      <TableCell>{p.uses || 0} / {p.max_uses || '∞'}</TableCell>
                      <TableCell><Badge variant={status.variant}>{status.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => { setEditPromo(p); setIsDialogOpen(true); }} data-testid={`edit-promo-${p.promo_id}`}>Modifier</Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(p.promo_id)} data-testid={`delete-promo-${p.promo_id}`}>Supprimer</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editPromo?.promo_id ? "Modifier le code promo" : "Nouveau code promo"}</DialogTitle>
              </DialogHeader>
              <PromoForm promo={editPromo} onSave={handleSave} onCancel={() => setIsDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

const Stars = ({ rating, size = "w-4 h-4", onSelect = null, hovered = 0, onHover = null }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        className={`${size} ${onSelect ? 'cursor-pointer' : ''} ${i <= (hovered || Math.round(rating)) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
        onClick={onSelect ? () => onSelect(i) : undefined}
        onMouseEnter={onHover ? () => onHover(i) : undefined}
        onMouseLeave={onHover ? () => onHover(0) : undefined}
        data-testid={onSelect ? `star-select-${i}` : undefined}
      />
    ))}
  </span>
);

const ReviewsSection = ({ productId }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    axios.get(`${API}/products/${productId}/reviews`).then(r => setData(r.data)).catch(console.error);
    if (user) {
      axios.get(`${API}/products/${productId}/reviews/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(r => setMine(r.data)).catch(console.error);
    } else {
      setMine(null);
    }
  };

  useEffect(load, [productId, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Choisissez une note en cliquant sur les étoiles"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${productId}/reviews`, { rating, comment }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success("Merci ! Votre avis sera publié après validation.");
      setRating(0);
      setComment('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  const pendingMine = mine?.review && !mine.review.approved;

  return (
    <div className="mt-16 border-t border-border pt-12" data-testid="reviews-section">
      <div className="flex items-center gap-4 mb-8">
        <h2 className="text-lg font-medium">Avis des élèves</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2">
            <Stars rating={data.average} />
            <span className="text-sm text-muted-foreground" data-testid="reviews-average">{data.average}/5 · {data.count} avis</span>
          </div>
        )}
      </div>

      {data.count === 0 && (
        <p className="text-muted-foreground text-sm mb-8" data-testid="no-reviews">Aucun avis publié pour le moment.</p>
      )}

      <div className="space-y-6 mb-10">
        {data.reviews.map(r => (
          <div key={r.review_id} className="bg-card border border-border rounded-lg p-5" data-testid={`review-${r.review_id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="font-medium">{r.user_name}</span>
              <Stars rating={r.rating} />
              <Badge variant="secondary" className="text-xs">Achat vérifié</Badge>
              <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
            {r.comment && <p className="text-sm text-muted-foreground leading-relaxed">{r.comment}</p>}
          </div>
        ))}
      </div>

      {user && mine?.eligible && (
        <div className="bg-muted/40 border border-border rounded-lg p-6 max-w-2xl" data-testid="review-form-block">
          <h3 className="font-medium mb-1">{mine.review ? "Modifier mon avis" : "Laisser un avis"}</h3>
          {pendingMine && (
            <p className="text-sm text-amber-700 mb-3" data-testid="review-pending-notice">Votre avis est en attente de validation. Vous pouvez le modifier ci-dessous.</p>
          )}
          <form onSubmit={submit} className="space-y-4 mt-3">
            <div className="flex items-center gap-3">
              <Label>Votre note :</Label>
              <Stars rating={rating} size="w-6 h-6" onSelect={setRating} hovered={hovered} onHover={setHovered} />
            </div>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Partagez votre expérience avec cette formation (facultatif)..."
              rows={4}
              data-testid="review-comment-input"
            />
            <Button type="submit" disabled={submitting} data-testid="submit-review">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {mine.review ? "Mettre à jour mon avis" : "Envoyer mon avis"}
            </Button>
          </form>
        </div>
      )}
      {user && mine && !mine.eligible && (
        <p className="text-sm text-muted-foreground" data-testid="review-not-eligible">Seuls les élèves ayant acheté cette formation peuvent laisser un avis.</p>
      )}
      {!user && (
        <p className="text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">Connectez-vous</Link> pour laisser un avis si vous avez suivi cette formation.
        </p>
      )}
    </div>
  );
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchReviews = () => {
    axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setReviews(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchReviews();
  }, [user, navigate]);

  const approve = async (reviewId) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/approve`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success("Avis publié");
      fetchReviews();
    } catch (error) {
      toast.error("Erreur lors de la publication");
    }
  };

  const remove = async (reviewId) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success("Avis supprimé");
      fetchReviews();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const pendingCount = reviews.filter(r => !r.approved).length;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-reviews">
      <div className="flex">
        <aside className="w-64 bg-card border-r border-border min-h-screen p-6 hidden lg:block">
          <h2 className="text-lg font-semibold mb-8">Administration</h2>
          <nav className="space-y-2">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><BarChart3 className="w-4 h-4" /> Tableau de bord</Link>
            <Link to="/admin/categories" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Tags className="w-4 h-4" /> Catégories</Link>
            <Link to="/admin/products" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Package className="w-4 h-4" /> Produits</Link>
            <Link to="/admin/orders" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><FileText className="w-4 h-4" /> Commandes</Link>
            <Link to="/admin/promos" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Ticket className="w-4 h-4" /> Codes promo</Link>
            <Link to="/admin/reviews" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary"><Star className="w-4 h-4" /> Avis</Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"><Users className="w-4 h-4" /> Utilisateurs</Link>
          </nav>
        </aside>

        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-light">Avis des élèves</h1>
            {pendingCount > 0 && <Badge data-testid="pending-reviews-count">{pendingCount} en attente</Badge>}
          </div>

          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Formation</TableHead>
                  <TableHead>Élève</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Commentaire</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucun avis pour le moment.</TableCell></TableRow>
                )}
                {reviews.map(r => (
                  <TableRow key={r.review_id} data-testid={`admin-review-${r.review_id}`}>
                    <TableCell className="max-w-[180px] truncate">{r.product_title}</TableCell>
                    <TableCell>{r.user_name}</TableCell>
                    <TableCell><Stars rating={r.rating} /></TableCell>
                    <TableCell className="max-w-[260px]"><span className="text-sm text-muted-foreground line-clamp-2">{r.comment || '—'}</span></TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell><Badge variant={r.approved ? 'default' : 'secondary'}>{r.approved ? 'Publié' : 'En attente'}</Badge></TableCell>
                    <TableCell className="text-right">
                      {!r.approved && (
                        <Button variant="ghost" size="sm" className="text-primary" onClick={() => approve(r.review_id)} data-testid={`approve-review-${r.review_id}`}>Publier</Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(r.review_id)} data-testid={`delete-review-${r.review_id}`}>Supprimer</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </main>
      </div>
    </div>
  );
};

// App Router
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<><Header /><HomePage /><Footer /></>} />
      <Route path="/catalogue" element={<><Header /><CataloguePage /><Footer /></>} />
      <Route path="/product/:id" element={<><Header /><ProductPage /><Footer /></>} />
      <Route path="/cart" element={<><Header /><CartPage /><Footer /></>} />
      <Route path="/login" element={<><Header /><LoginPage /><Footer /></>} />
      <Route path="/forgot-password" element={<><Header /><ForgotPasswordPage /><Footer /></>} />
      <Route path="/reset-password" element={<><Header /><ResetPasswordPage /><Footer /></>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/success" element={<><Header /><PaymentSuccessPage /><Footer /></>} />
      <Route path="/orders" element={<><Header /><OrdersPage /><Footer /></>} />
      <Route path="/account" element={<><Header /><AccountPage /><Footer /></>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
      <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/promos" element={<AdminRoute><AdminPromos /></AdminRoute>} />
      <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
